import React, { useState } from 'react';
import LogRocket from 'logrocket';
import { View, Text, TouchableOpacity, TextInput, StyleSheet, ActivityIndicator, Image } from 'react-native';
import AsyncStorage from '../AsyncStorageShim';
import ListsManager from '../services/ListsManager';
import { getAuth, signInWithEmailAndPassword, sendPasswordResetEmail, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from 'react-i18next';

let THEME = {};
const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: THEME.bg || 'var(--bg-deep-void)',
    padding: 24,
    position: 'relative',
  },
  backgroundContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },
  formCard: {
    width: '100%',
    maxWidth: 450,
    backgroundColor: THEME.bgCard || 'var(--bg-card)',
    borderRadius: 32,
    padding: 40,
    alignItems: 'center',
    boxShadow: 'var(--shadow-glass)',
    backdropFilter: 'blur(30px)',
    borderWidth: 1,
    borderColor: THEME.border || 'rgba(255, 255, 255, 0.08)',
    zIndex: 1,
  },
  logoContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 32,
    boxShadow: '0 0 20px rgba(59, 130, 246, 0.4)',
    borderWidth: 1,
    borderColor: 'rgba(59, 130, 246, 0.3)',
  },
  logo: {
    width: 100,
    height: 100,
    borderRadius: 50,
  },
  title: {
    fontSize: 32,
    fontWeight: '700',
    color: THEME.textPrimary || 'var(--text-primary)',
    marginBottom: 8,
    textAlign: 'center',
    fontFamily: 'Outfit, sans-serif',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: 16,
    color: THEME.textSecondary || 'var(--text-secondary)',
    marginBottom: 40,
    textAlign: 'center',
    fontWeight: '400',
  },
  errorText: {
    color: 'var(--accent-warning)',
    marginBottom: 24,
    textAlign: 'center',
    backgroundColor: 'rgba(244, 63, 94, 0.1)',
    borderRadius: 12,
    padding: 12,
    width: '100%',
    borderWidth: 1,
    borderColor: 'rgba(244, 63, 94, 0.2)',
  },
  successText: {
    color: 'var(--accent-success)',
    marginBottom: 24,
    textAlign: 'center',
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    borderRadius: 12,
    padding: 12,
    width: '100%',
    borderWidth: 1,
    borderColor: 'rgba(16, 185, 129, 0.2)',
  },
  inputContainer: {
    width: '100%',
    marginBottom: 16,
    gap: 16,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: THEME.inputBg || 'rgba(255, 255, 255, 0.03)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: THEME.inputBorder || 'rgba(255, 255, 255, 0.1)',
    transition: 'all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1)',
  },
  iconContainer: {
    paddingHorizontal: 20,
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1,
    borderRightColor: THEME.border || 'rgba(255, 255, 255, 0.05)',
    height: 60,
  },
  iconText: {
    fontSize: 20,
    opacity: 0.8,
  },
  input: {
    flex: 1,
    height: 60,
    color: THEME.inputText || 'var(--text-primary)',
    paddingHorizontal: 20,
    fontSize: 16,
    outlineStyle: 'none',
    backgroundColor: 'transparent',
  },
  forgotPasswordContainer: {
    alignSelf: 'flex-end',
    marginBottom: 32,
    marginTop: 8,
  },
  forgotPasswordText: {
    color: 'var(--accent-primary)',
    fontSize: 14,
    fontWeight: '500',
    cursor: 'pointer',
  },
  loginButton: {
    backgroundColor: 'var(--accent-primary)',
    borderRadius: 16,
    padding: 18,
    width: '100%',
    alignItems: 'center',
    marginBottom: 32,
    boxShadow: '0 8px 20px rgba(59, 130, 246, 0.4)',
    cursor: 'pointer',
    transition: 'transform 0.1s',
  },
  loginButtonText: {
    color: 'white',
    fontWeight: '700',
    fontSize: 16,
    letterSpacing: '0.05em',
  },
  contactContainer: {
    marginTop: 0,
    alignItems: 'center',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: THEME.border || 'rgba(255, 255, 255, 0.05)',
    width: '100%',
  },
  contactText: {
    color: THEME.textSecondary || 'var(--text-secondary)',
    fontSize: 13,
    textAlign: 'center',
    lineHeight: 20,
  },
  contactLink: {
    color: 'var(--accent-primary)',
    textDecorationLine: 'none',
    fontWeight: '600',
    cursor: 'pointer',
  },
});
let styles = getStyles();

const LoginScreen = ({ onLogin }) => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  Object.assign(THEME, theme);
  styles = getStyles();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const handleLogin = async () => {
    // Clear previous errors and reset states
    setError('');
    setResetEmailSent(false);

    // Validate inputs
    if (!email || !password) {
      setError(t('app.auth.error_fill_all'));
      return;
    }

    setLoading(true);

    try {
      // Sign in with Firebase authentication and set 30-day persistence
      const auth = getAuth();

      // Set persistence to LOCAL (30 days)
      await setPersistence(auth, browserLocalPersistence);

      // Sign in user
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Save user info to AsyncStorage
      await AsyncStorage.setItem('userToken', user.uid);
      await AsyncStorage.setItem('name', user.displayName || email.split('@')[0]);
      await AsyncStorage.setItem('email', user.email);

      LogRocket.identify(user.uid, {
        name: user.displayName || email.split('@')[0],
        email: user.email,
      });

      // Call the onLogin prop to update app state
      if (onLogin) {
        onLogin();
      } else {
        // Force reload as a fallback if onLogin not provided
        window.location.reload();
      }
    } catch (err) {
      console.error('Login error:', err);
      // Handle specific Firebase auth errors with user-friendly messages
      let errorMessage = t('app.auth.error_generic');

      if (err.code === 'auth/invalid-email') {
        errorMessage = t('app.auth.error_invalid_email');
      } else if (err.code === 'auth/user-not-found') {
        errorMessage = t('app.auth.error_user_not_found');
      } else if (err.code === 'auth/wrong-password') {
        errorMessage = t('app.auth.error_wrong_password');
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = t('app.auth.error_too_many_requests');
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    // Validate email
    if (!email) {
      setError(t('app.auth.reset_email_prompt'));
      return;
    }

    setLoading(true);
    setError('');
    setResetEmailSent(false);

    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, email);
      setResetEmailSent(true);
    } catch (err) {
      console.error('Password reset error:', err);

      // Handle specific Firebase auth errors
      let errorMessage = t('app.auth.reset_email_failed');

      if (err.code === 'auth/invalid-email') {
        errorMessage = t('app.auth.error_invalid_email');
      } else if (err.code === 'auth/user-not-found') {
        errorMessage = t('app.auth.error_user_not_found');
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.formCard}>
        <View style={styles.logoContainer}>
          <Image
            source={require('../assets/images/ReactFirstR.png')}
            style={styles.logo}
            resizeMode="contain"
          />
        </View>

        <Text style={styles.title}>{t('app.auth.title')}</Text>
        <Text style={styles.subtitle}>{t('app.auth.subtitle')}</Text>

        {error ? <Text style={styles.errorText}>{error}</Text> : null}
        {resetEmailSent && <Text style={styles.successText}>{t('app.auth.reset_email_sent')}</Text>}

        <View style={styles.inputContainer}>
          <View style={styles.inputWrapper}>
            <View style={styles.iconContainer}>
              <Text style={styles.iconText}>✉️</Text>
            </View>
            <TextInput
              style={styles.input}
              placeholder={t('app.auth.email')}
              placeholderTextColor={theme.inputPlaceholder}
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              keyboardType="email-address"
            />
          </View>

          <View style={styles.inputWrapper}>
            <View style={styles.iconContainer}>
              <Text style={styles.iconText}>🔒</Text>
            </View>
            <TextInput
              style={styles.input}
              placeholder={t('app.auth.password')}
              placeholderTextColor={theme.inputPlaceholder}
              value={password}
              onChangeText={setPassword}
              secureTextEntry
            />
          </View>
        </View>

        <TouchableOpacity
          style={styles.forgotPasswordContainer}
          onPress={handlePasswordReset}
        >
          <Text style={styles.forgotPasswordText}>{t('app.auth.forgot_password')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.loginButton}
          onPress={handleLogin}
          disabled={loading}
        >
          {loading ? (
            <ActivityIndicator color="white" size="small" />
          ) : (
            <Text style={styles.loginButtonText}>{t('app.auth.login')}</Text>
          )}
        </TouchableOpacity>

        <View style={styles.contactContainer}>
          <Text style={styles.contactText}>
            {t('app.auth.help_text')}{' '}
            <Text
              style={styles.contactLink}
              onPress={() => window.open('mailto:zohar@reactfirst.ai', '_blank')}
            >
              zohar@reactfirst.ai
            </Text>
            {' '}{t('app.auth.help_or')}{' '}
            <Text
              style={styles.contactLink}
              onPress={() => window.open('https://reactfirst.ai', '_blank')}
            >
              reactfirst.ai
            </Text>
          </Text>
        </View>
      </View>
    </View>
  );
};


export default LoginScreen; 